import { VarietyError } from '@src/CommonComponents/ErrorContent/ErrorContent.tsx'
import { useEventsStore } from '@src/Store/eventsStore.ts'
import { apiClient } from '@src/httpClient/api.ts'
import { useEffect } from 'react'
import { create } from 'zustand'

const PING_TIMEOUT = 5000
const PING_INTERVAL = 5000
const DEBOUNCE_MS = 2000
const POLL_SINCE_KEY = 'events_poll_since'

type NetState = {
    online: boolean
    setOnline: (v: boolean) => void
    checkOnline: () => void
    start: () => void
    stop: () => void

    timeoutMs: number
    debounceMs: number
    lastFlipAt: number
    latencyMs?: number
    lastChangeAt?: number
    error?: VarietyError
    clearError?: () => void

    intervalMs: number
}

let inited = false

export const useNet = create<NetState>()((set, get) => ({
    // online: false,
    online: typeof navigator !== 'undefined' ? navigator.onLine : false,
    setOnline: v => set({ online: v }),

    timeoutMs: PING_TIMEOUT,
    debounceMs: DEBOUNCE_MS,
    lastFlipAt: 0,
    latencyMs: 0,
    intervalMs: PING_INTERVAL,
    clearError: () => set({ error: undefined }),
    checkOnline: async () => {
        const { timeoutMs, lastFlipAt, debounceMs } = get()
        if (!navigator.onLine) {
            const now = Date.now()
            if (now - lastFlipAt >= debounceMs) {
                set({
                    online: false,
                    latencyMs: undefined,
                    lastChangeAt: now,
                    error: {
                        status: 'error',
                        message: 'navigator.offline'
                    },
                    lastFlipAt: now
                })
            }
            return
        }

        const since = localStorage.getItem(POLL_SINCE_KEY) || new Date().toISOString()
        const controller = new AbortController()
        const t = setTimeout(() => controller.abort(), timeoutMs)
        const started = performance.now()

        try {
            const search = new URLSearchParams({ since })
            const resp = await apiClient.api.tasEventsPollRetrieve({ since }, {search: search.toString(),  signal: controller.signal })
            clearTimeout(t)

            const rtt = Math.max(0, Math.round(performance.now() - started))
            const now = Date.now()

            localStorage.setItem(POLL_SINCE_KEY, new Date().toISOString())

            if (resp.data.has_changes) {
                useEventsStore.getState().refetch?.()
            }

            if (now - lastFlipAt >= debounceMs) {
                set({
                    online: true,
                    latencyMs: rtt,
                    lastChangeAt: now,
                    error: undefined
                })
            }
        } catch (error: any) {
            clearTimeout(t)
            const now = Date.now()
            if (now - lastFlipAt >= debounceMs) {
                set({
                    online: false,
                    latencyMs: 0,
                    lastChangeAt: now,
                    error: error?.name === 'AbortError' ? 'timeout' : String(error)
                })
            }
        }
    },
    start: () => {
        if (timer !== null) return
        const { intervalMs } = get()
        // навешиваем системные события один раз на всё приложение
        if (!inited) {
            inited = true

            const onOnline = () => get().checkOnline() // при восстановлении сразу проверяем
            const onOffline = () => get().checkOnline() // при падении тоже
            const onVisibility = () => {
                // при возврате во вкладку — мгновенная проверка
                if (document.visibilityState === 'visible') get().checkOnline()
            }
            const onFocus = () => get().checkOnline()

            window.addEventListener('online', onOnline)
            window.addEventListener('offline', onOffline)
            document.addEventListener('visibilitychange', onVisibility)
            window.addEventListener('focus', onFocus)
        }

        // первая проверка прямо сейчас
        get().checkOnline()

        // периодический опрос
        timer = window.setInterval(() => {
            // пауза при свернутой вкладке (экономим батарейку/пакеты)
            if (document.visibilityState === 'visible') {
                get().checkOnline()
            }
        }, intervalMs)
    },

    stop: () => {
        if (timer !== null) {
            clearInterval(timer)
            timer = null
        }
    }
}))

let timer: number | null = null
let attached = false

export function attachNetworkListeners() {
    if (attached || typeof window === 'undefined') return
    attached = true

    const setOnline = useNet.getState().setOnline

    setOnline(typeof navigator !== 'undefined' ? navigator.onLine : false)

    window.addEventListener('online', async () => {
        setOnline(true)
    })

    window.addEventListener('offline', () => {
        setOnline(false)
    })
}

export const useNetworkWatcher = () => {
    const start = useNet(s => s.start)
    const stop = useNet(s => s.stop)
    const clearError = useNet(s => s.clearError)

    useEffect(() => {
        start()
        return () => stop()
    }, [start, stop])

    const online = useNet(s => s.online)
    const latency = useNet(s => s.latencyMs)
    const error = useNet(s => s.error)

    return { online, latency, error, clearError }
}
